import React, { useMemo, useState } from 'react';
import {
  PageContent,
  openNotificationWithIcon,
  useAssetParams,
  useAssetConfig,
  useAssetQuery,
  useAssetTranslation,
  createUrl,
} from '@mss/mss-component-library';
import {
  useCollectorsQuery,
  EnrichedCollector,
} from '../../../../asset/services/queries';
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  Select,
  Switch,
  Table,
  notification,
} from 'antd';
import { useTranslation } from 'react-i18next';
import Fetch from '../../../util/_fetch';

type DataFormat = 'int' | 'float' | 'str' | 'bool';
type Action = 'none' | 'multiply' | 'divide' | 'siemens_ms';
type ComponentType = 'power_supply' | 'rack' | 'slot';

type TagMapping = {
  enabled: boolean;
  data_field: string;
  data_format: DataFormat;
  node_id: string;
  action: Action;
  action_value: number;
};

type RackSlotRow = {
  rack_nr: number;
  component_type: ComponentType;
  component_label: string;
  slot_nr: number | null;
  node_id: string;
  action: 'none' | 'siemens_ms';
  _enabled: boolean;
};

const ACTION_OPTIONS: { value: Action; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'divide', label: 'Divide' },
];

const STATUS_ACTION_OPTIONS: { value: Action; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'siemens_ms', label: 'Siemens MS' },
];

const DATA_FORMAT_LABELS: Record<DataFormat, string> = {
  int: 'Integer',
  float: 'Float',
  str: 'String',
  bool: 'Boolean',
};

const FIXED_CPU_ROWS: { data_field: string; label: string; data_format: DataFormat }[] = [
  { data_field: 'current_cpu_load', label: 'Current CPU load', data_format: 'float' },
  { data_field: 'average_cpu_load', label: 'Average CPU load', data_format: 'float' },
];

const FIXED_MEMORY_ROWS: { data_field: string; label: string; data_format: DataFormat }[] = [
  { data_field: 'work_memory_code_used',  label: 'Work Memory Code - Used',  data_format: 'int' },
  { data_field: 'work_memory_code_total', label: 'Work Memory Code - Total', data_format: 'int' },
  { data_field: 'work_memory_data_used',  label: 'Work Memory Data - Used',  data_format: 'int' },
  { data_field: 'work_memory_data_total', label: 'Work Memory Data - Total', data_format: 'int' },
];

const FIXED_RACK_COMPONENTS: {
  component_type: ComponentType;
  component_label: string;
}[] = [
  { component_type: 'power_supply', component_label: 'PSU Status 1' },
  { component_type: 'power_supply', component_label: 'PSU Status 2' },
  { component_type: 'rack', component_label: 'Rack Status' },
];

const MAX_SLOTS_PER_RACK = 8;

const defaultTagMapping = (data_field = '', data_format: DataFormat = 'float'): TagMapping => ({
  enabled: false,
  data_field,
  data_format,
  node_id: '',
  action: 'none',
  action_value: 1,
});

function readTagMappings(collector?: EnrichedCollector): TagMapping[] {
  const raw = (collector?.extra_options?.opc_config?.tag_mappings ?? []) as any[];
  return raw.map(row => ({
    enabled: row.enabled ?? true,
    data_field: row.data_field ?? '',
    data_format: (row.data_format ?? 'float') as DataFormat,
    node_id: row.node_id ?? '',
    action: (row.action ?? 'none') as Action,
    action_value:
      typeof row.action_value === 'number' ? row.action_value : 1,
  }));
}

function mergeFixedRows(
  fixed: { data_field: string; label: string; data_format: DataFormat }[],
  stored: TagMapping[]
): TagMapping[] {
  return fixed.map(({ data_field, data_format }) => {
    const existing = stored.find(x => x.data_field === data_field);
    return existing
      ? { ...defaultTagMapping(data_field, data_format), ...existing, data_field, data_format }
      : defaultTagMapping(data_field, data_format);
  });
}

function readRackSlotConfig(collector?: EnrichedCollector): RackSlotRow[] {
  const raw = (collector?.extra_options?.rack_slot_config ?? []) as any[];
  return raw.map(row => ({
    rack_nr: row.rack_nr ?? 1,
    component_type: (row.component_type ?? 'slot') as ComponentType,
    component_label: row.component_label ?? '',
    slot_nr: row.slot_nr ?? null,
    node_id: row.node_id ?? '',
    action: (row.action === 'siemens_ms' ? 'siemens_ms' : 'none') as 'none' | 'siemens_ms',
    _enabled: true,
  }));
}

type SaveFn = (
  collector: EnrichedCollector,
  extraOptionsPatch: Record<string, any>
) => Promise<void>;

function sanitizeExtraOptions(
  extraOptions: Record<string, any>
): Record<string, any> {
  return extraOptions;
}

export default function OPCConfiguration() {
  const { t } = useAssetTranslation();
  const [tCommon] = useTranslation('common');
  const { assetType, assetSubType, assetId } = useAssetParams();
  const { matchingConfig } = useAssetConfig(assetSubType);
  const { data: asset, isLoading: isLoadingAsset } = useAssetQuery(
    !matchingConfig
  );

  const query = useCollectorsQuery({
    presetUrl: createUrl({ assetType, assetSubType }, 'collectors/', true),
    enabledUrl: assetId
      ? `/api/assets/collector/?asset_id=${assetId}`
      : undefined,
    disabled: !asset,
  });

  const [saving, setSaving] = useState<string | null>(null);

  const collectorsByKey = useMemo(() => {
    const typeKey =
      assetSubType === 'siemens_s7' ? 'siemens_s7' : 'siemens_generic';
    const list: EnrichedCollector[] = query.collectors.data[typeKey] ?? [];
    const map: Record<string, EnrichedCollector | undefined> = {};
    list.forEach(c => {
      map[c.collector_key] = c;
    });
    return map;
  }, [query.collectors.data, assetSubType]);

  const saveCollector: SaveFn = async (collector, extraOptionsPatch) => {
    if (!assetId) return;
    setSaving(collector.collector_key);
    try {
      const mergedExtraOptions = sanitizeExtraOptions({
        ...(collector.extra_options ?? {}),
        ...extraOptionsPatch,
      });

      const response = await Fetch.put('/api/assets/collector/', {
        method: 'PUT',
        body: JSON.stringify({
          collector_type: collector.collector_type,
          collector_key: collector.collector_key,
          asset: assetId,
          enabled: collector.enabled,
          interval: collector.interval > 0 ? collector.interval : 1440,
          extra_options: mergedExtraOptions,
        }),
      });

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          detail =
            typeof body === 'string'
              ? body
              : body?.detail ?? JSON.stringify(body);
        } catch {
          // response was not JSON
        }
        openNotificationWithIcon(
          notification.error,
          t('Saving configuration failed'),
          detail,
          4
        );
        return;
      }

      await query.collectors.refetch();
      openNotificationWithIcon(
        notification.success,
        t('Configuration saved'),
        '',
        3
      );
    } catch (e) {
      openNotificationWithIcon(
        notification.error,
        t('Saving configuration failed'),
        e instanceof Error ? e.message : '',
        4
      );
    } finally {
      setSaving(null);
    }
  };

  if (!asset && !isLoadingAsset) {
    return (
      <PageContent title={t('OPC Configuration')}>
        <p>{tCommon('Loading')}...</p>
      </PageContent>
    );
  }

  return (
    <PageContent title={t('OPC Configuration')}>
      {assetSubType === 'siemens_s7' && (
        <S7Sections
          collectors={collectorsByKey}
          saving={saving}
          onSave={saveCollector}
          t={t}
        />
      )}
      {assetSubType === 'siemens_generic' && (
        <GenericMaintenanceSection
          key={
            collectorsByKey['maintenance_status']?.id ?? 'maintenance-pending'
          }
          collector={collectorsByKey['maintenance_status']}
          saving={saving === 'maintenance_status'}
          onSave={saveCollector}
          t={t}
        />
      )}
    </PageContent>
  );
}

function S7Sections({
  collectors,
  saving,
  onSave,
  t,
}: {
  collectors: Record<string, EnrichedCollector | undefined>;
  saving: string | null;
  onSave: SaveFn;
  t: (key: string) => string;
}) {
  const cpu = collectors['cpu'];
  const memory = collectors['memory'];
  const maintenance = collectors['maintenance_status'];

  return (
    <>
      {cpu && (
        <FixedTagMappingsSection
          key={cpu.id ?? 'cpu-pending'}
          title="CPU"
          collector={cpu}
          fixedRows={FIXED_CPU_ROWS}
          saving={saving === 'cpu'}
          onSave={onSave}
          t={t}
        />
      )}
      {memory && (
        <FixedTagMappingsSection
          key={memory.id ?? 'memory-pending'}
          title="Memory"
          collector={memory}
          fixedRows={FIXED_MEMORY_ROWS}
          saving={saving === 'memory'}
          onSave={onSave}
          t={t}
        />
      )}
      {maintenance && (
        <StatusSection
          key={maintenance.id ?? 'status-pending'}
          collector={maintenance}
          saving={saving === 'maintenance_status'}
          onSave={onSave}
          t={t}
        />
      )}
    </>
  );
}

function FixedTagMappingsSection({
  title,
  collector,
  fixedRows,
  saving,
  onSave,
  t,
  actionOptions = ACTION_OPTIONS,
}: {
  title: string;
  collector: EnrichedCollector;
  fixedRows: { data_field: string; label: string; data_format: DataFormat }[];
  saving: boolean;
  onSave: SaveFn;
  t: (key: string) => string;
  actionOptions?: { value: Action; label: string }[];
}) {
  const stored = useMemo(() => readTagMappings(collector), [collector]);
  const [rows, setRows] = useState<TagMapping[]>(() =>
    mergeFixedRows(fixedRows, stored)
  );

  const labelByField = useMemo(() => {
    const map: Record<string, string> = {};
    fixedRows.forEach(r => {
      map[r.data_field] = r.label;
    });
    return map;
  }, [fixedRows]);

  const update = (index: number, field: keyof TagMapping, value: any) => {
    setRows(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleSave = () => {
    onSave(collector, { opc_config: { tag_mappings: rows } });
  };

  return (
    <div className="mb-8" data-ta={`OPCConfiguration-${collector.collector_key}`}>
      <h4 className="mb-4">{t(title)}</h4>
      <Table<TagMapping & { _index: number }>
        dataSource={rows.map((row, i) => ({ ...row, _index: i }))}
        rowKey="_index"
        pagination={false}
        size="small"
        columns={[
          {
            title: t('Status'),
            dataIndex: 'enabled',
            width: 80,
            render: (val, record) => (
              <Switch
                checked={val}
                onChange={v => update(record._index, 'enabled', v)}
              />
            ),
          },
          {
            title: t('Data field'),
            dataIndex: 'data_field',
            render: (val: string) => <span>{t(labelByField[val] ?? val)}</span>,
          },
          {
            title: t('Data format'),
            dataIndex: 'data_format',
            width: 120,
            render: (val: DataFormat) => (
              <span>{t(DATA_FORMAT_LABELS[val] ?? val)}</span>
            ),
          },
          {
            title: t('OPC Item ID'),
            dataIndex: 'node_id',
            render: (val: string, record) => (
              <Input
                value={val}
                onChange={e =>
                  update(record._index, 'node_id', e.target.value)
                }
                placeholder="ns=2;s={..}"
              />
            ),
          },
          {
            title: t('Modifier'),
            dataIndex: 'action',
            width: 140,
            render: (val: Action, record) => (
              <Select
                value={val}
                onChange={v => update(record._index, 'action', v)}
                style={{ width: '100%' }}
                options={actionOptions.map(o => ({
                  value: o.value,
                  label: t(o.label),
                }))}
              />
            ),
          },
          {
            title: t('Modify value'),
            dataIndex: 'action_value',
            width: 120,
            render: (val: number, record) => (
              <InputNumber
                value={val}
                onChange={v => update(record._index, 'action_value', v ?? 1)}
                min={0}
                step={0.1}
                style={{ width: '100%' }}
                disabled={rows[record._index].action === 'none' || rows[record._index].action === 'siemens_ms'}
              />
            ),
          },
        ]}
      />
      <div className="mt-2 flex gap-2">
        <Button
          type="primary"
          size="small"
          loading={saving}
          onClick={handleSave}
        >
          {t('Save configuration')}
        </Button>
      </div>
    </div>
  );
}

function StatusSection({
  collector,
  saving,
  onSave,
  t,
}: {
  collector: EnrichedCollector;
  saving: boolean;
  onSave: SaveFn;
  t: (key: string) => string;
}) {
  const stored = useMemo(() => readRackSlotConfig(collector), [collector]);

  const [rack1, setRack1] = useState<RackSlotRow[]>(() =>
    buildRackRows(1, stored)
  );
  const [rack2, setRack2] = useState<RackSlotRow[] | null>(() => {
    const existing = stored.filter(r => r.rack_nr === 2);
    return existing.length ? buildRackRows(2, stored) : null;
  });

  const handleSave = () => {
    const payload: any[] = [];
    const push = (rows: RackSlotRow[]) => {
      rows.forEach(r => {
        if (!r._enabled) return;
        const base: any = {
          rack_nr: r.rack_nr,
          component_type: r.component_type,
          component_label: r.component_label,
          node_id: r.node_id,
          action: r.action,
        };
        if (r.component_type === 'slot') base.slot_nr = r.slot_nr;
        payload.push(base);
      });
    };
    push(rack1);
    if (rack2) push(rack2);
    onSave(collector, { rack_slot_config: payload });
  };

  return (
    <div className="mb-8" data-ta="OPCConfiguration-maintenance_status">
      <h4 className="mb-4">{t('Status')}</h4>
      <RackBlock
        title={`${t('Rack')} 1`}
        rows={rack1}
        setRows={setRack1}
        t={t}
      />
      <div className="mb-4">
        <Checkbox
          checked={!!rack2}
          onChange={e =>
            setRack2(e.target.checked ? buildRackRows(2, []) : null)
          }
        >
          {t('Add Rack 2')}
        </Checkbox>
      </div>
      {rack2 && (
        <RackBlock
          title={`${t('Rack')} 2`}
          rows={rack2}
          setRows={setRack2 as React.Dispatch<React.SetStateAction<RackSlotRow[]>>}
          t={t}
        />
      )}
      <div className="mt-2 flex gap-2">
        <Button
          type="primary"
          size="small"
          loading={saving}
          onClick={handleSave}
        >
          {t('Save configuration')}
        </Button>
      </div>
    </div>
  );
}

function buildRackRows(rackNr: number, stored: RackSlotRow[]): RackSlotRow[] {
  const forRack = stored.filter(r => r.rack_nr === rackNr);
  const fixed: RackSlotRow[] = FIXED_RACK_COMPONENTS.map(fc => {
    const existing = forRack.find(
      r =>
        r.component_type === fc.component_type &&
        r.component_label === fc.component_label
    );
    return (
      existing ?? {
        rack_nr: rackNr,
        component_type: fc.component_type,
        component_label: fc.component_label,
        slot_nr: null,
        node_id: '',
        action: 'none' as const,
        _enabled: false,
      }
    );
  });
  const slots = forRack
    .filter(r => r.component_type === 'slot')
    .slice(0, MAX_SLOTS_PER_RACK);
  return [...fixed, ...slots];
}

function RackBlock({
  title,
  rows,
  setRows,
  t,
}: {
  title: string;
  rows: RackSlotRow[];
  setRows: React.Dispatch<React.SetStateAction<RackSlotRow[]>>;
  t: (key: string) => string;
}) {
  const slotCount = rows.filter(r => r.component_type === 'slot').length;
  const rackNr = rows[0]?.rack_nr ?? 1;

  const update = (
    index: number,
    field: keyof RackSlotRow,
    value: any
  ) => {
    setRows(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addSlot = () => {
    if (slotCount >= MAX_SLOTS_PER_RACK) return;
    setRows(prev => [
      ...prev,
      {
        rack_nr: rackNr,
        component_type: 'slot',
        component_label: `Slot ${slotCount + 1}`,
        slot_nr: slotCount + 1,
        node_id: '',
        action: 'none' as const,
        _enabled: false,
      },
    ]);
  };

  const removeSlot = (index: number) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h5 className="m-0">{title}</h5>
      </div>
      <Table<RackSlotRow & { _index: number }>
        dataSource={rows.map((row, i) => ({ ...row, _index: i }))}
        rowKey="_index"
        pagination={false}
        size="small"
        columns={[
          {
            title: t('Status'),
            dataIndex: '_enabled',
            width: 80,
            render: (val: boolean, record) => (
              <Switch
                checked={val}
                onChange={v => update(record._index, '_enabled', v)}
              />
            ),
          },
          {
            title: t('Data field'),
            dataIndex: 'component_label',
            render: (val: string) => <span>{t(val)}</span>,
          },
          {
            title: t('Slot nr.'),
            dataIndex: 'slot_nr',
            width: 100,
            render: (val: number | null, record) =>
              record.component_type === 'slot' ? (
                <InputNumber
                  value={val ?? undefined}
                  onChange={v => update(record._index, 'slot_nr', v ?? null)}
                  min={1}
                  style={{ width: '100%' }}
                />
              ) : (
                <span>-</span>
              ),
          },
          {
            title: t('Data format'),
            dataIndex: 'data_format',
            width: 120,
            render: () => <span>{t('Integer')}</span>,
          },
          {
            title: t('OPC Item ID'),
            dataIndex: 'node_id',
            render: (val: string, record) => (
              <Input
                value={val}
                onChange={e =>
                  update(record._index, 'node_id', e.target.value)
                }
                placeholder="ns=2;s={..}"
              />
            ),
          },
          {
            title: t('Modifier'),
            dataIndex: 'action',
            width: 140,
            render: (val: 'none' | 'siemens_ms', record) => (
              <Select
                value={val}
                onChange={v => update(record._index, 'action', v)}
                style={{ width: '100%' }}
                options={STATUS_ACTION_OPTIONS.map(o => ({
                  value: o.value,
                  label: t(o.label),
                }))}
              />
            ),
          },
          {
            title: t('Modify value'),
            dataIndex: 'action_value',
            width: 120,
            render: () => (
              <InputNumber value={undefined} disabled style={{ width: '100%' }} />
            ),
          },
          {
            title: '',
            width: 60,
            render: (_: unknown, record) =>
              record.component_type === 'slot' ? (
                <Button
                  type="link"
                  danger
                  size="small"
                  onClick={() => removeSlot(record._index)}
                >
                  {t('Remove')}
                </Button>
              ) : null,
          },
        ]}
      />
      <div className="mt-2">
        <Button
          type="primary"
          size="small"
          onClick={addSlot}
          disabled={slotCount >= MAX_SLOTS_PER_RACK}
        >
          {t('Add slot')}
        </Button>
      </div>
    </div>
  );
}

const FIXED_MAINTENANCE_ROWS: { data_field: string; label: string; data_format: DataFormat }[] = [
  { data_field: 'maintenance_status', label: 'Maintenance Status', data_format: 'int' },
];

function GenericMaintenanceSection({
  collector,
  saving,
  onSave,
  t,
}: {
  collector: EnrichedCollector | undefined;
  saving: boolean;
  onSave: SaveFn;
  t: (key: string) => string;
}) {
  if (!collector) {
    return (
      <p>{t('No OPC-configurable collectors found for this asset.')}</p>
    );
  }

  return (
    <FixedTagMappingsSection
      title="Maintenance Status"
      collector={collector}
      fixedRows={FIXED_MAINTENANCE_ROWS}
      saving={saving}
      onSave={onSave}
      t={t}
      actionOptions={STATUS_ACTION_OPTIONS}
    />
  );
}
